import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { map, Observable } from 'rxjs';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';
import {
  RouteSummary,
  RouteSummaryResponse,
} from 'src/app/core/models/operations';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import {
  ColumnIds,
  ColumnText,
  EXCEL_FILE_NAME,
  ExcelHeader,
  ExportSummaryType,
} from './summary.constants';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  standalone: false,
})
export class SummaryComponent implements OnInit, OnChanges {
  readonly SELECT_ALL_TEXT = STATUS_OPTIONS.ALL_SELECTED;
  readonly IMAGES = IMAGE_SRC;

  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  @Input()
  summaryData$!: Observable<RouteSummaryResponse>;

  @Input()
  selectedDate!: string;

  @Input()
  exportExcelEvent!: EventEmitter<boolean>;

  @Input()
  addRouteEvent!: EventEmitter<RouteSummary>;

  tableData: RouteSummary[] = [];
  scanCodes!: Observable<string>;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly ColumnIds = ColumnIds;
  readonly HEADERS = ColumnText;

  // Sorting flags
  sortTotalDeliveryStops = false;
  sortCompletedDeliveryStops = false;
  sortPendingDeliveryStops = false;
  sortTotalDeliveryVolume = false;
  sortCompletedDeliveryVolume = false;
  sortTotalPickupStops = false;
  sortCompletedPickupStops = false;
  sortPendingPickupStops = false;
  sortCompletedPickupVolume = false;
  sortPendingDeliveryVolume = false;
  sortRouteCompletionStops = false;
  sortRouteCompletionVolume = false;

  tableRowElement!: HTMLCollection;

  // Route variables
  routeData = this.commonUtils.createTableColumn(ColumnIds.ROUTE);
  routeDetails: string[] = [];

  dataSource = new MatTableDataSource<RouteSummary>();
  originalDataSource!: RouteSummary[];
  destroy = inject(DestroyRef);

  routeDetailsLink = '';
  originalTableData!: RouteSummary[];

  constructor(public readonly commonUtils: CommonUtils) {}

  ngOnInit(): void {
    // Export event
    this.exportExcelEvent
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((event) => {
          if (event) {
            this.exportExcel();
          }
        }),
      )
      .subscribe();

    // Add route event
    this.addRouteEvent
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((event: RouteSummary) => {
        this.refreshRows(event);
      });
  }

  /** Handles changes to input properties and updates table data */
  ngOnChanges(changes: SimpleChanges) {
    const changedSummaryData: Observable<RouteSummaryResponse> | undefined =
      changes.summaryData$?.currentValue;

    if (changedSummaryData) {
      // Clear the route search textbox on new dataset
      this.routeData.formControl.setValue('', { emitEvent: true });

      changedSummaryData
        .pipe(
          takeUntilDestroyed(this.destroy),
          map((data: RouteSummaryResponse) => {
            const tableData = data.response.routesummary;

            this.dataSource.filterPredicate = this.commonUtils.createFilter();
            this.dataSource.data = tableData;
            this.originalDataSource = tableData;
            this.originalTableData = tableData;

            if (this.sort) {
              this.dataSource.sort = this.sort;
            }

            // Important fix: ensure table isn't filtered by stale value
            this.dataSource.filter = '';
            this.dataSource._updateChangeSubscription?.();

            this.customSorting(ColumnIds.TOTAL_DELIVERY_STOPS, false);
          }),
        )
        .subscribe();
    }
  }

  search() {
    this.commonUtils.searchFilter([this.routeData], this.dataSource);
  }

  /** Sorts table data by nested property in specified direction */
  customSorting(column: ColumnIds, sortDirection: boolean) {
    const parentId = column.split('.')[0] as keyof object;
    const childId = column.split('.')[1];

    this.dataSource.data.sort((a, b) => {
      const currentCompleted = (a as any)?.[parentId]?.[childId] ?? 0;
      const nextCompleted = (b as any)?.[parentId]?.[childId] ?? 0;
      if (sortDirection) {
        return nextCompleted - currentCompleted;
      }
      return currentCompleted - nextCompleted;
    });
    this.dataSource._updateChangeSubscription();
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(this.dataSource, this.originalTableData, [
      this.routeData.formControl,
    ]);
  }

  refreshRows(newValue: RouteSummary) {
    const data = this.dataSource.data;
    data.push(newValue);
    this.dataSource.data = data;
  }

  exportExcel(): void {
    this.commonUtils.exportExcel(
      ExcelHeader,
      EXCEL_FILE_NAME,
      this.simplifyDataSource(),
    );
  }

  getRouteDetailsLink(summary: RouteSummary): string {
    return 'route-details/' + summary.route;
  }

  private simplifyDataSource(): ExportSummaryType[] {
    const temp: ExportSummaryType[] = [];

    this.dataSource.filteredData.forEach((item, i) => {
      if (this.dataSource.filteredData.length !== i) {
        temp.push({
          route: item.route,
          totalDeliveryStops: item.deliveryStops.total,
          completedDeliveryStops: item.deliveryStops.completed,
          pendingDeliveryStops: item.deliveryStops.pending,
          totalDeliveryVolume: item.deliveryVolume.total,
          completedDeliveryVolume: item.deliveryVolume.completed,
          pendingDeliveryVolume: item.deliveryVolume.pending,
          totalPickupStops: item.pickupStops?.total,
          completedPickupStops: item.pickupStops?.completed,
          pendingPickupStops: item.pickupStops?.pending,
          completedPickupVolume: item.pickupVolume?.completed,
        });
      }
    });

    return temp;
  }
}
